import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Clock, Video, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const STATUS_STYLE = {
  confirmed: "bg-primary-fixed text-primary-on-container",
  completed: "bg-surface-container-high text-on-surface-variant",
  cancelled: "bg-secondary-container text-secondary-on-container",
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [reschedule, setReschedule] = useState(null);
  const [days] = useState(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) { const d = new Date(now); d.setDate(now.getDate() + i); arr.push(d); }
    return arr;
  });
  const [rDay, setRDay] = useState(null);
  const [rSlots, setRSlots] = useState([]);
  const [rSlot, setRSlot] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/bookings");
    setBookings(r.data.sort((a, b) => new Date(b.startTime) - new Date(a.startTime)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const upcoming = bookings.filter((b) => b.status === "confirmed" && new Date(b.startTime) > now);
  const past = bookings.filter((b) => !(b.status === "confirmed" && new Date(b.startTime) > now));

  const canModify = (b) => new Date(b.startTime) - now > 24 * 3600 * 1000;

  const cancel = async (b) => {
    try {
      await api.put(`/bookings/${b.booking_id}/cancel`, { reason: "Client cancelled" });
      toast.success("Session cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const openReschedule = (b) => { setReschedule(b); setRDay(null); setRSlots([]); setRSlot(null); };

  const loadRSlots = async (d) => {
    setRDay(d); setRSlot(null);
    const r = await api.get("/booking/slots", { params: { date: dateKey(d), timezone_str: tz } });
    setRSlots(r.data.slots || []);
  };

  const submitReschedule = async () => {
    try {
      await api.put(`/bookings/${reschedule.booking_id}/reschedule`, { startUtc: rSlot.startUtc });
      toast.success("Session rescheduled");
      setReschedule(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule");
    }
  };

  const Card = ({ b }) => (
    <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-ambient" data-testid="booking-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status] || "bg-surface-container"}`}>{b.status}</span>
          <p className="font-headline text-xl text-primary mt-3">{b.sessionType}</p>
          <p className="text-sm text-on-surface-variant mt-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {fmt(b.startTime)}</p>
        </div>
        {b.status === "confirmed" && new Date(b.startTime) > now && (
          <a href={b.meetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-fixed text-primary-on-container text-sm font-medium shrink-0 hover:opacity-90 transition-opacity duration-300" data-testid="booking-join">
            <Video className="w-4 h-4" strokeWidth={1.5} /> Join
          </a>
        )}
      </div>
      {b.status === "confirmed" && new Date(b.startTime) > now && (
        <div className="mt-5 flex gap-3">
          <button disabled={!canModify(b)} onClick={() => openReschedule(b)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-surface-container-low text-primary hover:bg-primary-fixed transition-colors duration-300 disabled:opacity-40" data-testid="booking-reschedule">
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Reschedule
          </button>
          <button disabled={!canModify(b)} onClick={() => cancel(b)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-surface-container-low text-secondary hover:bg-secondary-container transition-colors duration-300 disabled:opacity-40" data-testid="booking-cancel">
            <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Cancel
          </button>
          {!canModify(b) && <span className="text-xs text-outline self-center">Locked within 24h</span>}
        </div>
      )}
    </div>
  );

  return (
    <AppShell active="bookings">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-headline font-light text-primary tracking-tight">Your sessions</h1>
        <p className="mt-3 text-on-surface-variant">Reschedule or cancel up to 24 hours before start.</p>
      </div>

      <section className="mb-10">
        <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Upcoming</h2>
        {upcoming.length === 0 ? (
          <div className="bg-surface-container-low rounded-3xl p-8 text-center text-on-surface-variant" data-testid="no-upcoming">No upcoming sessions yet.</div>
        ) : (
          <div className="space-y-4">{upcoming.map((b) => <Card key={b.booking_id} b={b} />)}</div>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-widest text-outline mb-4">Past & cancelled</h2>
          <div className="space-y-4 opacity-90">{past.map((b) => <Card key={b.booking_id} b={b} />)}</div>
        </section>
      )}

      <Dialog open={!!reschedule} onOpenChange={(o) => !o && setReschedule(null)}>
        <DialogContent className="rounded-3xl border-0 bg-surface-container-lowest max-w-lg" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-headline text-primary text-2xl font-light">Reschedule session</DialogTitle>
          </DialogHeader>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {days.map((d) => {
              const isSel = rDay && dateKey(d) === dateKey(rDay);
              return (
                <button key={d.toISOString()} onClick={() => loadRSlots(d)} className={`shrink-0 w-14 h-18 py-2 rounded-2xl flex flex-col items-center justify-center transition-colors duration-300 ${isSel ? "zenith-cta text-white" : "bg-surface-container-low text-on-surface hover:bg-primary-fixed"}`} data-testid="reschedule-day">
                  <span className="text-[10px] uppercase opacity-70">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                  <span className="font-headline text-xl">{d.getDate()}</span>
                </button>
              );
            })}
          </div>
          {rDay && (
            rSlots.length === 0 ? <p className="text-sm text-on-surface-variant py-4 text-center">No openings this day.</p> : (
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto no-scrollbar">
                {rSlots.map((s) => (
                  <button key={s.startUtc} onClick={() => setRSlot(s)} className={`min-h-[44px] rounded-xl text-sm transition-colors duration-300 ${rSlot?.startUtc === s.startUtc ? "zenith-cta text-white" : "bg-surface-container-low text-primary hover:bg-primary-fixed"}`} data-testid="reschedule-slot">{s.localTime}</button>
                ))}
              </div>
            )
          )}
          <DialogFooter>
            <button onClick={submitReschedule} disabled={!rSlot} className="w-full py-3.5 rounded-2xl zenith-cta text-white font-medium disabled:opacity-40 hover:opacity-90 transition-opacity duration-300" data-testid="reschedule-confirm">Confirm new time</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
